import { noop } from 'lodash';
import { memo, useCallback } from 'react';

import { Command, useCommandState } from '@air/cmdk';

import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { SearchCommandGroup } from '~/components/SearchCommand/SearchCommandGroup';
import { SearchCommandSimilarImageSearch } from '~/components/SearchCommand/SearchCommandSimilarImageSearch';
import { SearchCommandSuggestionListItem } from '~/components/SearchCommand/SearchCommandSuggestionListItem';
import { SmartSearchPromotionDropdown } from '~/components/SearchCommand/SmartSearchPromotionDropdown';
import { getHasSeenSmartSearchPromotion } from '~/utils/SearchUtils';

export type SearchCommandSuggestionListProps = {
  isOpen: boolean;
  onSelect?: (suggestion: string) => void;
  suggestions?: string[];
  onSimilarImageSearch: (base64Image: string) => void;
};

export const SmartSearchCommandList = memo(
  ({ isOpen, onSelect, onSimilarImageSearch }: SearchCommandSuggestionListProps) => {
    const search = useCommandState((state) => state.search);
    const { similarToImageSearchAvailable } = usePrivateSearchUIHelpers();

    const showSmartSearchPromotion = !getHasSeenSmartSearchPromotion() && isOpen;

    const onSelectSearch = useCallback(() => {
      onSelect?.(search);
    }, [onSelect, search]);

    return search || similarToImageSearchAvailable ? (
      <div className="grow overflow-y-auto rounded-8 border border-grey-6 bg-grey-1">
        <Command.List className="flex flex-col gap-0 px-4 py-3" data-testid="SEARCH_COMMAND_LIST">
          {search && (
            <SearchCommandGroup heading={search ? undefined : 'Search suggestions'}>
              <SearchCommandSuggestionListItem suggestion={search} onSelect={onSelectSearch} />
            </SearchCommandGroup>
          )}
          {similarToImageSearchAvailable && <SearchCommandSimilarImageSearch onSearch={onSimilarImageSearch} />}
        </Command.List>
      </div>
    ) : showSmartSearchPromotion ? (
      // TODO: Follow-up in https://linear.app/air/issue/COR-1149/add-learn-more-mechanism-to-smart-search-promotional-dropdown
      <SmartSearchPromotionDropdown onLearnMoreClick={noop} />
    ) : null;
  },
);

SmartSearchCommandList.displayName = 'SmartSearchCommandList';
